// Cookie-consent preference, persisted in a cookie shared across *.bitquery.io
// apps (same scoping approach as theme.ts). Consent is opt-in: until the user
// decides, analytics is treated as NOT granted, so the trackers (Google
// Analytics, Microsoft Clarity) stay off. Mirrors theme.ts' module-store shape
// so a choice re-renders every useSyncExternalStore consumer.

const COOKIE_KEY = 'bq-cookie-consent' // cookie shared across *.bitquery.io subdomains
const CURRENT_VERSION = 1 // bump to re-prompt everyone when the cookie list changes

// What the user can toggle. "necessary" is implied and always on, so only the
// optional categories live here.
export type CookieConsent = {
    analytics: boolean
}

type StoredConsent = CookieConsent & { v: number }

// On a real bitquery.io host, scope the cookie to the whole domain so the
// choice is shared across every subdomain. On localhost / preview hosts, omit
// the domain so it's a normal host-only cookie. (Same logic as theme.ts.)
function cookieDomainAttr(): string {
    try {
        const host = window.location.hostname || ''
        if (host === 'bitquery.io' || host.endsWith('.bitquery.io')) return '; domain=.bitquery.io'
    } catch {
        /* noop */
    }
    return ''
}

function readCookie(): CookieConsent | null {
    try {
        const match = document.cookie.match(/(?:^|;\s*)bq-cookie-consent=([^;]+)/)
        if (!match) return null
        const parsed = JSON.parse(decodeURIComponent(match[1])) as Partial<StoredConsent>
        // A version mismatch means our cookie categories changed — treat as
        // "not decided" so the banner re-appears and re-collects consent.
        if (parsed?.v === CURRENT_VERSION && typeof parsed.analytics === 'boolean') {
            return { analytics: parsed.analytics }
        }
    } catch {
        /* missing or malformed cookie */
    }
    return null
}

function writeCookie(consent: CookieConsent): void {
    try {
        const secure = window.location.protocol === 'https:' ? '; Secure' : ''
        const payload: StoredConsent = { v: CURRENT_VERSION, analytics: consent.analytics }
        const value = encodeURIComponent(JSON.stringify(payload))
        // 1 year; readable by JS (no HttpOnly) so each app gates its own trackers client-side.
        document.cookie = `${COOKIE_KEY}=${value}; path=/; max-age=31536000${cookieDomainAttr()}; SameSite=Lax${secure}`
    } catch {
        /* noop */
    }
}

// ---------------------------------------------------------------------------
// Module-level store, shared by every useCookieConsent() consumer (banner +
// tracker loaders) so a single decision re-renders all of them.
// ---------------------------------------------------------------------------

export type ConsentState = {
    decided: boolean // has the user made a choice yet?
    analytics: boolean // is analytics granted?
}

const initial = readCookie()
let state: ConsentState = {
    decided: initial !== null,
    analytics: initial?.analytics ?? false,
}
const listeners = new Set<() => void>()

export function subscribeConsent(listener: () => void): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

// Stable reference between changes — required by useSyncExternalStore.
export function getConsentState(): ConsentState {
    return state
}

// Imperative read for non-React callers (e.g. one-shot tracker loaders).
export function analyticsAllowed(): boolean {
    return state.decided && state.analytics
}

export function setConsent(consent: CookieConsent): void {
    writeCookie(consent)
    state = { decided: true, analytics: consent.analytics }
    listeners.forEach((l) => l())
}
